import { nanoid } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ProductServices from '../../services/products';

import { addSkus } from '../../data/testData';

export const getProductList = createAsyncThunk(
  'products/getProductList',
  async ({ date, route, division, isExpected }, { rejectWithValue }) => {
    try {
      const res = await ProductServices.getAllProducts(
        date,
        route,
        division,
        isExpected
      );

      // Check if the response has a StatusCode indicating an error
      if (res && res.StatusCode === 400) {
        return rejectWithValue({
          message: res.Value,
          statusCode: res.StatusCode,
        });
      }

      // Extract route-level data from Value
      const value = res.Value || {};
      const warehouseId = value.WarehouseId;
      const warehouseName = value.WarehouseName;
      const driverName = value.DriverName;
      const divisionValue = value.Division;
      const isExpectedOnly = value.IsExpectedOnly;
      const routeStatus = value.RouteStatus;
      const TimeStamp = value.TimeStamp;
      const UserName = value.UserName;
      const EmployeeName = value.EmployeeName;
      const EmployeeNumber = value.EmployeeNumber;
      const RouteNeedToReturnQuantity = value.RouteNeedToReturnQuantity;
      const RouteReturnedQuantity = value.RouteReturnedQuantity;
      const RTRNCounts = value.RTRNCounts || {
        AddOnCount: 0,
        CrossLoadCount: 0,
        OverLoadCount: 0,
      };

      // Extract order data (products)
      const products = Array.isArray(value.OrderData) ? value.OrderData : [];

      return {
        products,
        routeStatus,
        warehouseId,
        warehouseName,
        driverName,
        division: divisionValue,
        isExpectedOnly,
        TimeStamp,
        UserName,
        EmployeeName,
        EmployeeNumber,
        RouteNeedToReturnQuantity,
        RouteReturnedQuantity,
        RTRNCounts,
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || 'An unexpected error occurred',
        statusCode: error.response?.status || 500,
      });
    }
  }
);

export const initialState = {
  routeStatus: '',
  routeCloseData: null,
  productList: [],
  filteredProductList: [],
  skuList: addSkus,
  isProductsLoading: false,
  hasLoadedSuccessful: false,
  isFirstLoad: true,
  employeeId: '',
  error: null,
  previousSearchValues: {
    date: '',
    route: '',
    division: '',
    isExpected: true,
  },
  RTRNCounts: {
    AddOnCount: 0,
    CrossLoadCount: 0,
    OverLoadCount: 0,
  },
  warehouseId: '',
  warehouseName: '',
  driverName: '',
  division: '',
  routeNeedToReturnQuantity: null,
  routeReturnedQuantity: null,
  isExpectedOnly: true,
};

export const searchProductSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    resetProductList: (state) => {
      return {
        ...state,
        productList: [],
        error: null,
        warehouseId: '',
        warehouseName: '',
        routeNeedToReturnQuantity: null,
        routeReturnedQuantity: null,
        RTRNCounts: initialState.RTRNCounts,
      };
    },
    resetFilterList: (state) => {
      return {
        ...state,
        filteredProductList: [],
      };
    },
    searchOrders: (state, action) => {
      const searchOrdersFn = () => {
        const savedProductList = [...state.productList];
        const filteredList = [];

        savedProductList.forEach((p) => {
          if (
            p.SalesOrderNumber.toLowerCase().includes(
              action.payload.toLowerCase()
            )
          ) {
            filteredList.push(p);
          } else {
            p.SkuData.forEach((rd) => {
              if (
                rd.SkuNumber.toLowerCase().includes(
                  action.payload.toLowerCase()
                )
              )
                filteredList.push({
                  ...p,
                  SkuData: p.SkuData.filter((r) =>
                    r.SkuNumber.toLowerCase().includes(
                      action.payload.toLowerCase()
                    )
                  ),
                });
              if (
                rd.VendorDescription.toLowerCase().includes(
                  action.payload.toLowerCase()
                )
              )
                filteredList.push({
                  ...p,
                  SkuData: p.SkuData.filter((r) =>
                    r.VendorDescription.toLowerCase().includes(
                      action.payload.toLowerCase()
                    )
                  ),
                });
              if (
                rd.ReferenceColorFinish.toLowerCase().includes(
                  action.payload.toLowerCase()
                )
              )
                filteredList.push({
                  ...p,
                  SkuData: p.SkuData.filter((r) =>
                    r.ReferenceColorFinish.toLowerCase().includes(
                      action.payload.toLowerCase()
                    )
                  ),
                });
            });
          }
        });

        return [
          ...new Map(filteredList.map((f) => [f.SalesOrderNumber, f])).values(),
        ];
      };

      state.filteredProductList = searchOrdersFn();
    },
    updateSkuReturned: (state, action) => {
      //Payload must be the passed SKU ID from the selected checkbox
      const product = state.productList.find(
        (p) => p.id === action.payload.rowId
      );
      if (product) {
        const sku = product.skus.find((s) => s.id === action.payload.skuId);
        if (sku) {
          const isReturned = sku.returned;
          sku.returned = !isReturned;
        }
      }
    },
    addUnexpected: (state, action) => {
      //Payload must be an object {sku#, reasonOption}
      const newSku = {
        id: nanoid(),
        sku: action.payload.skuNum,
        description: action.payload.description,
      };
      state.skuList.push(newSku);
    },
    removeUnexpected: (state, action) => {
      //Payload must be the skuId
      const sku = state.skuList.findIndex((sku) => sku.id === action.payload);
      state.skuList.splice(sku, 1);
    },
    setPreviousSearch: (state, action) => {
      state.previousSearchValues = {
        date: action.payload.date,
        route: action.payload.route,
        division: action.payload.division,
        isExpected: action.payload.isExpected,
      };
    },
    setEmployeeId: (state, action) => {
      state.employeeId = action.payload;
    },
    setRouteStatus: (state, action) => {
      return {
        ...state,
        routeStatus: action.payload,
      };
    },
  },
  extraReducers: {
    [getProductList.fulfilled]: (state, action) => {
      const products = Array.isArray(action.payload.products)
        ? action.payload.products
        : [];
      return {
        ...state,
        routeCloseData: action.payload.TimeStamp
          ? {
              TimeStamp: action.payload.TimeStamp,
              UserName: action.payload.UserName,
              EmployeeName: action.payload.EmployeeName,
              EmployeeNumber: action.payload.EmployeeNumber,
            }
          : null,
        isProductsLoading: false,
        isFilterSearching: false,
        hasLoadedSuccessful: products.length > 0,
        isFirstLoad: false,
        productList: [...products],
        filteredProductList: [...products],
        routeStatus: action.payload.routeStatus,
        warehouseId: action.payload.warehouseId || '',
        warehouseName: action.payload.warehouseName || '',
        driverName: action.payload.driverName || '',
        division: action.payload.division || '',
        isExpectedOnly: action.payload.isExpectedOnly || false,
        routeNeedToReturnQuantity: action.payload.RouteNeedToReturnQuantity,
        routeReturnedQuantity: action.payload.RouteReturnedQuantity,
        RTRNCounts: action.payload.RTRNCounts || initialState.RTRNCounts,
        error: null,
      };
    },
    [getProductList.pending]: (state) => {
      return {
        ...state,
        hasLoadedSuccessful: false,
        isProductsLoading: true,
        error: null,
      };
    },
    [getProductList.rejected]: (state, action) => {
      return {
        ...state,
        hasLoadedSuccessful: false,
        isProductsLoading: false,
        isFirstLoad: false,
        error: action.payload?.message || 'An unexpected error occurred',
      };
    },
  },
});

export const {
  searchOrders,
  updateSkuReturned,
  addUnexpected,
  removeUnexpected,
  resetProductList,
  resetFilterList,
  setPreviousSearch,
  setEmployeeId,
  setRouteStatus,
} = searchProductSlice.actions;

export default searchProductSlice.reducer;
